import { ClaimFilesPageSize } from '@/api/claims'
import { Input, Typography, usePicker } from '@/components'
import { Pagination } from '@/components/molecules/Pagination'
import { ThemeColor } from '@/enums'
import { useSnackbar } from '@/providers'
import { useClaimFiles } from '@/services'
import { useDebouncedCallback } from '@/utils'
import { Button, DataTable, useDataTable } from '@surein/ui'
import { FileText, Upload, X } from 'lucide-react'
import { DragEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import createColumns from './columns'
import {
  DropZone,
  FilesContainer,
  RemoveStagedButton,
  StagedActions,
  StagedFileInfo,
  StagedFileRow,
  StagedFiles,
  StyledRelativeContainer,
  ToolbarRow,
} from './styles'

const fileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`

// Mirrors the limits usePicker enforces on the click-to-browse path, so drag
// & drop rejects the same selections.
const MAX_FILE_MB = 200
const MAX_FILES_PER_ADD = 10

type FilesTabProps = {
  companyId: string
  claimId: string
}

export const FilesTab = ({ companyId, claimId }: FilesTabProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const snackbar = useSnackbar()
  const [isDragOver, setIsDragOver] = useState(false)
  const [stagedFiles, setStagedFiles] = useState<File[]>([])

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, 200)

  const {
    files,
    totalItems,
    isLoading,
    isError,
    uploadFiles,
    isUploading,
    downloadFile,
  } = useClaimFiles(
    companyId,
    claimId,
    search,
    pagination.pageIndex + 1,
    pagination.pageSize as ClaimFilesPageSize,
  )

  const { openFilePicker, RefComponent } = usePicker({
    multiple: true,
    perFile: true,
  })

  const addFiles = (incoming: File[]) => {
    setStagedFiles((prev) => {
      const seen = new Set(prev.map(fileKey))
      return [...prev, ...incoming.filter((file) => !seen.has(fileKey(file)))]
    })
  }

  const removeStagedFile = (key: string) => {
    setStagedFiles((prev) => prev.filter((file) => fileKey(file) !== key))
  }

  const screenFiles = (candidates: File[]): File[] => {
    if (candidates.some((file) => file.size / 1024 ** 2 > MAX_FILE_MB)) {
      snackbar.error(t('upload_max_size', { maxMbSize: MAX_FILE_MB }))
      return []
    }
    if (candidates.length > MAX_FILES_PER_ADD) {
      snackbar.error(t('insurance_documents_upload_max'))
      return []
    }
    return candidates
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const dropped = screenFiles(Array.from(e.dataTransfer.files))
    if (dropped.length > 0) {
      addFiles(dropped)
    }
  }

  const handleUpload = () => {
    if (stagedFiles.length === 0 || isUploading) return
    const submittedKeys = new Set(stagedFiles.map(fileKey))
    uploadFiles(stagedFiles, {
      onSuccess: (result) => {
        const failedNames = new Set(result.failed)
        // Drop only the files that were part of this submission and succeeded;
        // keep submitted failures for retry and anything staged in the meantime.
        setStagedFiles((prev) =>
          prev.filter(
            (file) =>
              !submittedKeys.has(fileKey(file)) || failedNames.has(file.name),
          ),
        )
      },
    })
  }

  const columns = useMemo(() => createColumns(t, downloadFile), [
    t,
    downloadFile,
  ])

  const table = useDataTable({
    columns,
    data: files,
    configOverrides: {
      state: { pagination },
      onPaginationChange: setPagination,
      manualPagination: true,
      pageCount: Math.max(1, Math.ceil(totalItems / pagination.pageSize)),
    },
  })

  const hasData = files.length > 0

  return (
    <FilesContainer>
      <ToolbarRow>
        <Input
          placeholder={t('claimsPage.detail.files.searchPlaceholder')}
          onChange={(e) => debouncedSetSearch(e.target.value)}
        />
      </ToolbarRow>

      <RefComponent onPick={addFiles} />
      <DropZone
        $isDragOver={isDragOver}
        role="button"
        tabIndex={0}
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openFilePicker()
          }
        }}
        onDragOver={(e: DragEvent<HTMLDivElement>) => {
          e.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <Upload size={20} />
        <Typography variant="p2Body">
          {t('claimsPage.detail.files.dropzone')}
        </Typography>
      </DropZone>

      {stagedFiles.length > 0 && (
        <StagedFiles>
          {stagedFiles.map((file) => {
            const key = fileKey(file)
            return (
              <StagedFileRow key={key}>
                <StagedFileInfo>
                  <FileText size={16} />
                  <Typography ellipsis variant="p2Body">
                    {file.name}
                  </Typography>
                </StagedFileInfo>
                <RemoveStagedButton
                  onClick={() => removeStagedFile(key)}
                  aria-label={t('claimsPage.detail.files.removeStagedFile')}
                  disabled={isUploading}
                >
                  <X size={14} />
                </RemoveStagedButton>
              </StagedFileRow>
            )
          })}
          <StagedActions>
            <Button icon={Upload} onClick={handleUpload} disabled={isUploading}>
              {t('claimsPage.detail.files.uploadStaged', {
                count: stagedFiles.length,
              })}
            </Button>
          </StagedActions>
        </StagedFiles>
      )}

      {isLoading && (
        <Typography color={ThemeColor.b50} variant="p2Body">
          {t('claimsPage.detail.files.loading')}
        </Typography>
      )}

      {!isLoading && isError && (
        <Typography color={ThemeColor.b50} variant="p2Body">
          {t('claimsPage.detail.files.error')}
        </Typography>
      )}

      {!isLoading && !isError && (
        <StyledRelativeContainer $hasData={hasData}>
          <DataTable
            table={table}
            borderless
            emptyText={t('claimsPage.detail.files.empty')}
          />
          {hasData && (
            <Pagination
              pageIndex={pagination.pageIndex}
              pageCount={table.getPageCount()}
              onPageChange={(index) => table.setPageIndex(index)}
              onPageSizeChange={(size) => table.setPageSize(size)}
            />
          )}
        </StyledRelativeContainer>
      )}
    </FilesContainer>
  )
}
